import cached from 'utils/cached';
import formatDateTime from 'utils/formatDateTime';
import { accessNested, asList, arrayUnique, sortObjectByOrder, parseDate, commaSeparatedList } from 'utils/utils';
import { normalizeRegion, getShowtimesData } from 'utils/bolt';
import { getBaseFilters } from 'utils/bolt/showtimesLoader';
import parseQueryString from 'utils/parseQueryString';
import { api } from 'utils/bolt/api';
import hooks from 'utils/hooks';
import Store from 'store/store';

// TODO: expand EMEA where needed in expandAppData ? To avoid bloating the code
const emeaRegions = [
	'AT', 'BE', 'DK', 'EG', 'FI', 'FR', 'DE', 'GR', 'GG', 'HU', 'IE', 'IL', 'IT', 'JE', 'KW', 'LB',
	'LU', 'NL', 'NO', 'PL', 'PT', 'QA', 'SA', 'ZA', 'ES', 'SE', 'CH', 'TR', 'UA', 'AE', 'GB'
];
function expandRegions(regions) {
	let emea = regions.findIndex(e => e && e.toUpperCase() === 'EMEA');
	if (emea !== -1) {
		regions.splice(emea, 1, ...emeaRegions);
	}
	return regions;
}

const getAlternativePath = cached(() => accessNested(Store.get(), 'appData.urls.alternativeAPIPath') || '');
const transformPath = path => getAlternativePath() + path;

const todayString = () => formatDateTime(new Date(), 'yyyy-MM-dd');
const defaultTodayValue = () => ({ today: todayString() });
const defaultLocalTime = () => ({ local_time: formatDateTime(new Date(), 'yyyy-MM-dd HH:mm:ss') });

const defaultMovieOptions = async () => {
	let store = Store.get();
	// let selected = store.showtimes?.movieFilterId;
	let selected = store.showtimes?.filters?.selected?.movie;

	// let appData = store.appData;
	let baseList = getShowtimesData().movieId || '';

	let ids = baseList.split(',').map(e => e.trim()).filter(e => e);
	if (selected?.length) {
		// ids = selected.filter(value => ids.includes(value));
		ids = selected;
	}

	ids = await hooks.call('showtimes.request.filters.movie_ids', ids);

	return {
		movie_id: ids.join(',')
	};
};

const defaultLocationOptions = async () => {
	const countrySearch = getShowtimesData().countrySearch;
	let geo = await accessNested(Store.get(), 'client.geo.loading');
	geo = geo || accessNested(Store.get(), 'client.geo') || {};
	let geoObj = {
		lat: geo.lat,
		lon: geo.lon,
		approxPos: geo.label || (geo.city ? geo.city + ', ' + geo.countryCode : null),
	};
	if (countrySearch) {
		geoObj.approxPos = geo.city;
		geoObj.countryToSearch = (geo.label === geo.city) ? geo.countryCode : undefined;
	}
	return geoObj;
};

const defaultLimitOptions = () => {
	// let appData = Store.get().appData;
	let limit = getShowtimesData().limit;
	if (!+limit) {
		limit = 10;
	}
	return { limit: limit, offset: 0 };
};


const defaultRadiusOptions = () => {
	// let appData = Store.get().appData;
	let radius = getShowtimesData().radius;
	if (!+radius) {
		return {};
	}
	return { radius: +radius };
};

export function getBoundingRegions(page) {
	// TODO: per-page cache ?
	let store = Store.get();
	let appData = store.appData;
	let activePageRegions = accessNested(appData, ['pages', page || store.activePage, 'data', 'boundingRegions']);
	let regionsObject = activePageRegions || accessNested(appData, 'meta.boundingRegions');
	let regions = asList(regionsObject);
	if (!regions.includes(__REGION__) && (!regionsObject || regionsObject[__REGION__] !== false)) {
		regions.push(__REGION__);
	}
	if (regions.find(e => e?.toUpperCase?.() === 'INTL')) {
		return null;
	}
	return arrayUnique(expandRegions(regions).map(normalizeRegion).filter(e => e));
}

const defaultBoundingRegionsOptions = () => {
	return { countries: getBoundingRegions() };
};

let parseProviderValue = (val) => {
	if (typeof val === 'string') {
		return val.split(',').map(e => e.trim()).filter(e => e).join(',') || undefined;
	}
	if (typeof val === 'boolean') {
		return val.toString();
	}
	return undefined;
};

export function getProviderValues(providers, type) {
	return Object.keys(providers).reduce((full, region) => {
		if (providers[region] && /^[a-z]{2}$/i.test(region)) {
			full[region.toLowerCase()] = parseProviderValue(providers[region][type]);
		}
		return full;
	}, { base: parseProviderValue(providers[type]) });
}

function providerValuesToSimpleString(data) {
	if (!data) {
		return data;
	}
	return Object.entries(data).filter(e => e[1]).map(entry => entry.join(':')).join('|') || undefined;
}

const defaultShowtimesProviderOptions = () => {
	let storeData = Store.get().showtimes;
	const customProvider = storeData?.customDataProvider ? { base: storeData.customDataProvider } : null;
	let data = getShowtimesData();
	let deeplinks = customProvider || getProviderValues(data.providers || {}, 'deeplink');
	let showtimes = customProvider || getProviderValues(data.providers || {}, 'showtimes');
	let onlyWithDeeplink = getProviderValues(data.providers || {}, 'onlyWithDeeplink');
	let alwaysWithDeeplink = getProviderValues(data.providers || {}, 'alwaysWithDeeplink');

	// legacy + default
	if (!deeplinks.base) {
		deeplinks.base = parseProviderValue(data.deeplinkProviders || 'webedia');
	}

	const options = {
		deeplink_providers: providerValuesToSimpleString(deeplinks),
	};
	if (showtimes) {
		options.showtimes_providers = providerValuesToSimpleString(showtimes);
	}
	if (onlyWithDeeplink) {
		options.only_with_deeplink = providerValuesToSimpleString(onlyWithDeeplink);
	}
	if (alwaysWithDeeplink) {
		options.always_with_deeplink = providerValuesToSimpleString(alwaysWithDeeplink);
	}
	return options;
};

const defaultTicketProviderOptions = cached(() => {
	// let appData = Store.get().appData;
	let data = getShowtimesData();
	let providers = getProviderValues(data.providers || {}, 'tickets');
	// legacy
	if (!providers.base) {
		providers.base = parseProviderValue(data.ticketProviders);
	}
	return { ticket_providers: providers };
});

const defaultDisabledTheaterOptions = cached(() => {
	// const appData = Store.get().appData;
	const data = getShowtimesData();
	// Should not use parseProviderValue here or rename the function
	const disabledTheaters = parseProviderValue(data.disabledTheaters || '');
	if (typeof disabledTheaters === 'string') return { theaters_excluded: disabledTheaters.split(',') };
	return { theaters_excluded: disabledTheaters };
});

const filterNames = {
	audioDetails: 'audio_details',
	time: 'time_of_day',
	date: 'date',
	formats: 'formats',
	attributes: 'attributes',
	language: 'language',
	subtitles: 'subtitles'
};
const defaultFiltersOptions = async () => {
	let store = Store.get();
	let filters = { ...store?.showtimes?.filters?.selected };

	let formatsDict = getShowtimesData().formatsDict || {};
	let formatsAllowed = getShowtimesData().formatsAllowed || '';

	if (formatsAllowed) {
		formatsAllowed = formatsAllowed.split(',').map(x => x.trim());
	}

	filters = await hooks.call('showtimes.request.filters.preprocess', filters);

	// Changes the labels from formatsDict to symbols before sending back to the API
	if (filters.format?.length) {
		let getOriginalFormatSymbols = f => {
			const originalSymbols = Object.keys(formatsDict).filter(k => formatsDict[k] === f);
			return [f].concat(originalSymbols);
		};

		let translatedFormats = filters.format.flatMap(
			f => getOriginalFormatSymbols(f)
		).filter(f => !formatsAllowed || formatsAllowed.includes(f));

		filters.format = translatedFormats;
	} else if (formatsAllowed) {
		// Default to allowed formats if no filters are set
		filters.format = formatsAllowed;
	}

	let sentFilters = Object.keys(filters).reduce((sentFilters, key) => {
		if (key === 'date' && !store?.showtimes?.filters?.userSelectedDate) {
			return sentFilters;
		}
		let value = expandFilterSelection(filters[key], store.showtimes?.filters?.available[key]);
		if (key !== 'combinedLanguage') {
			sentFilters[filterNames[key] || key] = value;
		}
		return sentFilters;
	}, {});

	let combinedLanguageFilters;
	if (filters.combinedLanguage) {
		combinedLanguageFilters = filters.combinedLanguage.map(lang => ({
			and: [...Object.keys(lang).map(key => ({ [key]: lang[key] }))]
		}));
	}
	sentFilters = await hooks.call('showtimes.request.filters', sentFilters);
	return { filters: sentFilters, and: combinedLanguageFilters };
};

const defaultGroupBookingsOptions = () => {
	const { groupBookingsTabActive } = Store.get().showtimes;
	if (groupBookingsTabActive) {
		const showtimesData = getShowtimesData();
		// const qs = parseQueryString();
		// const chainFilter = qs.chain;
		// const chainData = showtimesData?.groupBookings?.formsForChain || {};
		// const neededChain = Object.keys(chainData).find(key => chainData[key].chainSlug === chainFilter);
		const neededChains = Object.keys(showtimesData?.groupBookings?.formsForChain || {});
		const ptrId = showtimesData?.groupBookings?.ptrId;

		return {
			chain_ids: neededChains.join(','),
			groupBookings: 'true',
			ptrId: ptrId
		};
	}
	return {};
};

export async function getShowtimes(options) {
	options = Object.assign(
		{},
		defaultLimitOptions(),
		await defaultMovieOptions(),
		await defaultLocationOptions(),
		defaultBoundingRegionsOptions(),
		defaultShowtimesProviderOptions(),
		defaultTicketProviderOptions(),
		defaultDisabledTheaterOptions(),
		defaultTodayValue(),
		defaultLocalTime(),
		defaultRadiusOptions(),
		defaultGroupBookingsOptions(),
		options
	);

	if (!options.filters) {
		Object.assign(options, await defaultFiltersOptions());
	}

	if (!options.lat || !options.lon) {
		throw new Error('No lat/lon available');
	}
	return await hooks.call('api.response.screenings', api(transformPath('/screenings/location'), options));
}

export async function getNearbyCinemas(options) {
	options = Object.assign(
		{},
		await defaultMovieOptions(),
		await defaultLocationOptions(),
		defaultBoundingRegionsOptions(),
		defaultShowtimesProviderOptions(),
		defaultTicketProviderOptions(),
		defaultDisabledTheaterOptions(),
		defaultTodayValue(),
		defaultLocalTime(),
		defaultRadiusOptions(),
		options,
	);

	if (!options.filters) {
		Object.assign(options, await defaultFiltersOptions());
	}

	if (!options.lat || !options.lon) {
		throw new Error('No lat/lon available');
	}

	// options.movie_ids = [options.movie_id];
	// delete options.movie_id;

	return api(transformPath('/chain/more'), options);
}

export async function getNearbyShowtimesLocations(options) {
	options = Object.assign(
		{},
		await defaultMovieOptions(),
		await defaultLocationOptions(),
		defaultBoundingRegionsOptions(),
		defaultShowtimesProviderOptions(),
		defaultDisabledTheaterOptions(),
		defaultTodayValue(),
		defaultRadiusOptions(),
		options
	);

	if (!options.filters) {
		Object.assign(options, await defaultFiltersOptions());
		if (options.filters) {
			delete options.filters.date;
		}
	}

	if (!options.lat || !options.lon) {
		throw new Error('No lat/lon available');
	}

	return api(transformPath('/screenings/more'), options);
}

export async function getAvailableFilters(options) {
	options = Object.assign(
		{},
		await defaultMovieOptions(),
		await defaultLocationOptions(),
		defaultBoundingRegionsOptions(),
		defaultShowtimesProviderOptions(),
		defaultTicketProviderOptions(),
		defaultDisabledTheaterOptions(),
		defaultTodayValue(),
		defaultLocalTime(),
		defaultRadiusOptions(),
		options
	);
	return api(transformPath('/filters'), options);
}

export async function getAvailableTheatersDates(options) {
	options = Object.assign(
		{},
		await defaultMovieOptions(),
		await defaultLocationOptions(),
		defaultBoundingRegionsOptions(),
		defaultShowtimesProviderOptions(),
		defaultTicketProviderOptions(),
		defaultDisabledTheaterOptions(),
		defaultTodayValue(),
		options
	);
	return api(transformPath('/filters'), options);
}


export async function getCitySuggestions(options) {
	options = Object.assign({}, await defaultMovieOptions(), await defaultLocationOptions(), defaultBoundingRegionsOptions(), options);
	if (!options.search || options.search.length < 3) {
		return { result: 'ok', response: [] };
	}
	return api('/cities/suggest', options);
}

// ---------------------------------------
//   End of API calls
// ---------------------------------------

function escapeRegExp(string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
export function expandFilterSelection(values, available) {
	if (!Array.isArray(values) || !Array.isArray(available)) {
		return values;
	}
	let globs = values.filter(v => v.includes('*'));
	if (!globs.length || getShowtimesData().disableFilterGlobs) {
		return values;
	}
	let ret = values.filter(v => !v.includes('*'));
	globs.forEach(glob => {
		let regex = new RegExp('^' + glob.split('*').map(escapeRegExp).join('(.*)') + '$', 'i');
		ret = ret.concat(available.filter(opt => regex.test(opt)));
	});
	return arrayUnique(ret);
}

async function expandFiltersAvailable(values = {}) {
	let base = await getBaseFilters();
	Object.keys(base).forEach(key => {
		values[key] = arrayUnique((values[key] || []).concat(base[key]));
	});
	return values;
}

export async function fetchTheatersDates(options = {}) {
	if (!options.api || !options.api.theaters_included) {
		return;
	}

	let theaterId = options.api.theaters_included;

	let previousTheatersDates = Store.get().showtimes.theatersDates;
	if (previousTheatersDates && previousTheatersDates[theaterId]) {
		return;
	}

	let theatersDates = Object.assign({}, previousTheatersDates, {
		[theaterId]: 'loading'
	});
	Store.get().showtimes.set({ theatersDates: theatersDates });

	let response = {};
	try {
		response = await getAvailableFilters(options.api);
	} catch (e) { }

	const data = response.response || {};
	let today = todayString();

	previousTheatersDates = Store.get().showtimes.theatersDates;
	theatersDates = Object.assign({}, previousTheatersDates, {
		[theaterId]: (data.dates || []).filter(e => e >= today)
	});
	Store.get().showtimes.set({ theatersDates: theatersDates });
}

export async function refreshFilters(options = {}) {
	// TODO: error handling
	let response;
	try {
		let apiOptions = await hooks.call('filters.request.options', options.api);
		response = await getAvailableFilters(apiOptions);
	} catch (e) { }

	return applyFiltersData(response?.response || {});
}
export async function applyFiltersData(data) {
	let formatsDict = getShowtimesData().formatsDict || {};
	let formatsAllowed = getShowtimesData().formatsAllowed || '';
	let movieIds = getShowtimesData().movieId || '';
	if (formatsAllowed) {
		formatsAllowed = formatsAllowed.split(',').filter(x => x.trim());
	}
	const usingV2Data = getShowtimesData().useV2;


	let selectedChanged = false;
	let today = todayString();

	let formats = await hooks.call('filters.formats.expand', data.formats || []);
	if (formatsAllowed) {
		formats = formats.filter(f => formatsAllowed.includes(f));
	}
	formats = arrayUnique(formats.map(f => formatsDict[f] || f));

	const screeningCategoriesExclusions = getShowtimesData().screeningCategories?.exclusions || {};

	let availableRaw = {
		date: (data.dates || []).filter(e => e >= today),
		time: data.time_of_day || [],
		// Transform the formats from filters and remove duplicates
		// if two formats have the same label
		format: formats,
		attributes: [],
		originalLanguage: undefined,
		language: undefined,
		subtitles: undefined,
		dubbing: undefined,
		overVoice: undefined,
		audioDetails: undefined,
	};

	if (!screeningCategoriesExclusions.attributes && data.attributes) {
		availableRaw.attributes = data.attributes;
	}

	if (!screeningCategoriesExclusions.language && usingV2Data && data.originalLanguage) {
		availableRaw.originalLanguage = data.originalLanguage;
	}

	if (!screeningCategoriesExclusions.language && usingV2Data && data.language) {
		availableRaw.language = data.language;
	}

	if (!screeningCategoriesExclusions.language && usingV2Data && data.subtitles) {
		availableRaw.subtitles = data.subtitles;
	}

	if (!screeningCategoriesExclusions.language && usingV2Data && data.dubbing) {
		availableRaw.dubbing = data.dubbing;
	}

	if (!screeningCategoriesExclusions.language && usingV2Data && data.overVoice) {
		availableRaw.overVoice = data.overVoice;
	}

	if (!screeningCategoriesExclusions.audioDetails && data.audio_details) {
		availableRaw.audioDetails = data.audio_details;
	}

	// if the platform has more than one movie
	if (movieIds.split(',').length > 1) {
		availableRaw.movie = movieIds.replace(/\s/g, '').split(',');// Array of available movie ids as a filter.
	}

	let available = await expandFiltersAvailable(availableRaw);
	available = await hooks.call('filters.expand', available);

	let newFilters = Store.get().showtimes.filters.set({ available, loading: false }).now();
	if (newFilters.selected) {
		Object.keys(newFilters.selected).forEach(filter => {
			let options = available[filter];
			// Date is handled lower
			if (!options || filter === 'date') {
				return;
			}
			let selectedList = newFilters.selected[filter];
			let newOptions = selectedList?.filter(option => option.includes('*') || options.includes(option));
			if (newOptions?.length !== selectedList?.length) {
				Store.get().showtimes.filters.selected[filter].reset(newOptions).now();
				selectedChanged = true;
			}
		});

		let newSelected = Store.get().showtimes.filters.selected;

		let currentDate = newSelected.date?.[0];
		let selectedDateAvailable = currentDate && available.date.includes(currentDate);
		if (currentDate && !selectedDateAvailable) {
			let firstDate = available.date[0] || today;
			firstDate = await hooks.call('filters.defaultDate', firstDate, { available });
			if (currentDate !== firstDate) {
				newSelected.set({ date: [firstDate] }).now();
				selectedChanged = true;
			}
		}
	}

	return selectedChanged;
}

const getTicketProvidersPerCountry = cached(() => {
	// let appData = Store.get().appData;
	let data = getShowtimesData();
	let providers = data.providers || {};
	let providersPerCountry = Object.keys(providers).reduce((full, region) => {
		if (providers[region] && /^[a-z]{2}$/i.test(region)) {
			full[region.toLowerCase()] = parseProviderValue(providers[region].tickets);
		}
		return full;
	}, { base: parseProviderValue(providers.tickets) });
	// legacy
	if (!providersPerCountry.base) {
		providersPerCountry.base = parseProviderValue(data.ticketProviders);
	}
	return providersPerCountry;
});
const getEnabledTicketProviders = (theater) => {
	let geo = theater.address.intl || theater.address.local;
	let country = geo && geo.country;
	let all = getTicketProvidersPerCountry();
	return all[country] || all.base || [];
};

export function processScreenings(theater, movies = {}, formatsDict = {}) {
	let ticketProviders = getEnabledTicketProviders(theater);
	const showtimesData = getShowtimesData();

	const formatsBeforeTransformation = theater.screenings.map(s => s.format);

	theater.screenings.forEach((screening) => {
		if (!screening.link) {
			screening.link = {};
		}
		if (!screening.link.direct && !screening.lockLinks) {
			let link = theater.url || theater.chain?.url;
			screening.link.direct = !/^(https?:)?\/\//.test(link) ? 'https://' + link : link;
		}
		if (ticketProviders.length) {
			Object.keys(screening.link).forEach(prov => {
				if (!ticketProviders.includes(prov)) {
					delete screening.link[prov];
				}
			});
		}
		if (screening.link.fandango) {
			try {
				const fandangoLink = new URL(screening.link.fandango);
				const fandangoAffiliateID = (showtimesData?.providers?.fandangoAffiliateID || '').trim();
				if (fandangoAffiliateID) fandangoLink.searchParams.set('a', fandangoAffiliateID);
				const fandangoCampaign = parseQueryString().cmp;
				if (fandangoCampaign) fandangoLink.searchParams.set('cmp', fandangoCampaign);
				screening.link.fandango = fandangoLink.href;
			} catch (e) {}
		}
		screening.movie = movies[screening.movieId];
		screening.format = formatsDict[screening.format] || screening.format;
	});

	// Delete duplicates created by assigning the same format label to screenings
	// screened at the same time for the same movie and theater
	const priorityRepeatedTimes = commaSeparatedList(formatsDict.priorityRepeatedTimes);
	if (priorityRepeatedTimes.length) {
		const getPriority = idx => {
			const priority = priorityRepeatedTimes.indexOf(formatsBeforeTransformation[idx]);
			return priority === -1 ? idx + priorityRepeatedTimes.length : priority;
		};

		theater.screenings = theater.screenings.filter((s1, idx1) => {
			return !theater.screenings.some((s2, idx2) => (
				s2.movie.id === s1.movie.id
				&& s2.date === s1.date
				&& s2.time === s1.time
				&& s2.format === s1.format
				&& getPriority(idx2) < getPriority(idx1)
			));
		});
	}
}

export async function refreshShowtimes(options = {}, numberOfRetries = 0) {
	let showtimesState = Store.get().showtimes;
	let formatsDict = getShowtimesData().formatsDict || {};
	let allMovies = showtimesState?.allMovies;

	showtimesState.set({ loading: true, error: false });
	let apiResponse;


	try {
		apiResponse = await getShowtimes(options.api);
	} catch (err) {
		console.error(err);
		let error = err?.message || err;
		Store.get().showtimes.set({ error, loading: false });
		return;
	}

	if (apiResponse?.response?.filters) {
		await applyFiltersData(apiResponse.response.filters);
	}

	if (apiResponse?.response?.filteredFilters) {
		Store.get().showtimes.set({ filteredFilters: apiResponse.response.filteredFilters });
	}

	if (numberOfRetries === 0 && !apiResponse?.response?.data?.length && apiResponse?.response?.filters?.dates?.length) {
		return refreshShowtimes({
			...options
		}, numberOfRetries + 1);
	}

	let cities = apiResponse?.response?.included?.allCities;
	if (cities) {
		cities.forEach(city => {
			if (city.cityName) {
				city.city = city.cityName;
				delete city.cityName;
			}
		});
		Store.get().showtimes.nearby.locations.set({
			data: cities,
			loading: false,
			more: false
		});
	}

	let data = apiResponse?.response?.data || [];
	let more = apiResponse?.response?.meta?.more || false;
	let movies = apiResponse?.response?.included?.movies || {};
	let chains = apiResponse?.response?.included?.chains || {};

	// Check if the server automatically picked a date for us (if we didn't specify one)
	// Assumes all screenings have the same date - just find the first one
	// (ignore "midnight screenings" for now), unless it's a coming soon

	// TODO: clean this up (handle midnight screenings in the same loop)
	let foundOne = false;
	for (let theater of data) {
		let screening = theater.screenings.find(s => s.date && (s.cta === 2 || ((!s.cta || s.cta === 1) && s.time >= '04:00')));
		if (screening) {
			foundOne = true;
			Store.get().showtimes.filters.selected.set('date', [screening.date]);
			break;
		}
	}
	// Only midnight screenings available
	if (!foundOne) {
		let firstScreening = data[0]?.screenings?.[0];
		if (firstScreening) {
			let cta = firstScreening?.cta || 1;
			let date = firstScreening?.date;
			if (cta === 1 && firstScreening.time < '04:00') {
				let tmpDate = parseDate(date);
				tmpDate.setDate(tmpDate.getDate() + 1);
				date = formatDateTime(tmpDate, 'yyyy-MM-dd');
			}
			Store.get().showtimes.filters.selected.set('date', [date]);
		}
	}

	data.forEach(theater => {
		theater.chain = chains[theater.chainId];
		processScreenings(theater, movies, formatsDict);
	});

	Store.get().showtimes.set({
		data,
		allMovies: !allMovies ? movies : allMovies,
		movies,
		filterSnapshot: { ...accessNested(Store.get(), 'showtimes.filters.selected') },
		more
	}).now();

	return data;
}

export async function clearAllShowtimesData() {
	await applyFiltersData({});
	Store.get().showtimes.set({
		loading: false,
		data: [],
		filteredFilters: {},
		filterSnapshot: { ...accessNested(Store.get(), 'showtimes.filters.selected') },
		more: false,
		nearby: {
			chain: {},
			locations: {},
		}
	}).now();
}

export async function getMore(options = {}) {
	let showtimesState = Store.get().showtimes;

	const apiOptions = Object.assign({
		offset: (showtimesState.data || []).length
	}, options.api);

	let apiResponse;
	try {
		apiResponse = await getShowtimes(apiOptions);
	} catch (err) {
		let error = (err && err.message) || err;
		Store.get().showtimes.set({ error, loadingMore: false });
		return;
	}

	let data = apiResponse.response.data;
	let more = accessNested(apiResponse.response, 'meta.more', false);

	let formatsDict = getShowtimesData().formatsDict || {};
	let movies = apiResponse.response.included.movies;
	let chains = apiResponse.response.included.chains;
	data.forEach(theater => {
		theater.chain = chains[theater.chainId];
		processScreenings(theater, movies, formatsDict);
	});

	const newData = [...showtimesState.data, ...data];
	Store.get().showtimes.set({
		data: newData,
		more
	}).now();
}

export function prioritiseTicketProviders(providers) {
	const priority = ['atom', 'movietickets', 'fandango', 'direct'];
	const showtimesData = getShowtimesData();
	const highlighted = showtimesData?.providers?.highlightedTicketProvider;
	return providers.sort((a, b) => {
		if (a === highlighted) {
			return -1;
		} else if (b === highlighted) {
			return 1;
		} else {
			return priority.indexOf(b) - priority.indexOf(a);
		}
	});
}

export function convertTimeTo12Hr(time) {
	time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];

	if (time.length > 1) {
		time = time.slice(1);
		time[5] = +time[0] < 12 ? ' AM' : ' PM';
		time[0] = +time[0] % 12 || 12;
	}
	return time.join('');
}

export function getActiveBanner(banners, userRegion) {
	if (!banners) return;
	let orderedBanners = sortObjectByOrder(banners);
	let activeBanner = orderedBanners.find(banner => {
		const regions = expandRegions((banner.regions || '').toUpperCase().split(',').map(e => e.trim()));
		return regions.includes(userRegion) || regions.includes('INTL');
	});

	const bannersWithRegions = Object.values(banners).filter(banner => banner.regions);
	if (!activeBanner && !bannersWithRegions.length) {
		activeBanner = orderedBanners[0];
	}

	return activeBanner;
}

// Getting single movie filter name for multimovie apps
export function getMovieName(movieId) {
	const store = Store.get();
	const currentRegion = store.client?.geo?.countryCode;
	const movieData = (getShowtimesData() || {}).movieData;
	const equivalentOption = store.showtimes?.allMovies?.[movieId] || store.showtimes?.movies?.[movieId];
	const localeMetaTitle = store.appData?.meta?.title?.locale;

	const currentRegionTitle =
		// 1. Thundr movieData Region override title
		movieData?.[currentRegion]?.[movieId]?.title
		// 2. Thundr movieData Top Level override title
		||	movieData?.[movieId]?.title
		// 3. Title coming from API for this region
		||	equivalentOption?.regions.find(movieId => movieId.country === currentRegion)?.title
		// 4. Title coming from API for INTL region
		|| equivalentOption?.regions.find(movieId => movieId.country === 'INTL')?.title
		// 5. Last resort - show the meta locale title for all
		|| localeMetaTitle
		// 6. Very last resort - the movie id itself
		|| movieId;
	return currentRegionTitle;
}

export function getFiltersOrderReference(option) {
	const store = Store.get();
	return (store.appData?.pages?.boltShowtimes?.data?.orderForFiltersAndScreenings?.[option] || '').split(',').map(a => a.trim()).filter(a => a).reverse();
}

export function getDummyFormDataForGroupBookings(formDate) {
	return __IS_LOCAL__ || __IS_DEMO__ ? {
		group: 'Corporate Group',
		meeting: 'Single Meeting',
		attendees: '33',
		chainId: '1',
		startTime: {
			hour: '3',
			minute: '30',
			meridian: 'PM'
		},
		email : 'dev@powster.com',
		state: 'Alabama (AL)',
		organizationName: 'School',
		currentStage: 'Check Your Details',
		firstname: 'Dummy',
		lastname: 'Test',
		address: '1 Test, Narnia, TE1 2ST',
		city: 'Narnia',
		zip: '12345',
		areaCode: '333',
		taxExempt: 'No',
		captcha: '',
		formDate: formDate,
	} : {};
}
